import { useQuery, useMutation } from "@sveltestack/svelte-query";
import { getProductPrices, getProductWithId } from "./priceCalculator";
const DEFAULT_HEADERS = {
    "Content-Type": "application/json",
    "X-Order-Origin": "Skekraft Website",
};
export const poll = async ({ fn, validate, interval, maxAttempts, signStatus, }) => {
    let attempts = 0;
    let timeoutId;
    const executePoll = async (resolve, reject) => {
        try {
            const unsubscribe = signStatus.subscribe((status) => {
                if (status === "STOP") {
                    cleanup();
                    return reject({ signError: true, message: "Polling cancelled" });
                }
            });
            const cleanup = () => {
                unsubscribe();
                clearTimeout(timeoutId);
            };
            let result;
            try {
                result = await fn(attempts);
            }
            catch (error) {
                cleanup();
                return reject(Object.assign(Object.assign({}, error), { signError: true }));
            }
            attempts++;
            if (validate(result)) {
                cleanup();
                return resolve(result);
            }
            else if (maxAttempts && attempts >= maxAttempts) {
                cleanup();
                return reject({ signError: true, message: "Max attempts reached" });
            }
            else {
                timeoutId = setTimeout(executePoll, interval, resolve, reject);
            }
        }
        catch (err) {
            return reject(Object.assign(Object.assign({}, err), { signError: true }));
        }
    };
    return new Promise(executePoll);
};
export const api = async (url, init, options) => {
    if (init === undefined) {
        init = {};
    }
    init.headers = Object.assign(DEFAULT_HEADERS, init.headers);
    if (init === null || init === void 0 ? void 0 : init.body) {
        init.body = JSON.stringify(init.body);
    }
    return fetch(url, init).then(async (response) => {
        const data = await response.json();
        if ((response.ok || (options === null || options === void 0 ? void 0 : options.allow[response.status])) &&
            ("success" in data ? data.success : true)) {
            return data;
        }
        else {
            return Promise.reject(Object.assign(Object.assign({}, data), { response_status: response.status }));
        }
    });
};
const setProductParameters = (url, data) => {
    for (const prop in data) {
        let value = data[prop];
        if (Array.isArray(value)) {
            value.forEach((v) => {
                url.append(`${prop}[]`, v);
            });
        }
        else {
            url.append(prop, value);
        }
    }
};
const createUrl = (searchParams) => {
    const urlPath = searchParams.charAt(0) === "?" ? searchParams : "?" + searchParams;
    return new URL(urlPath, window.location.origin);
};
export const Products = {
    getProduct: async (id, estimation, params) => {
        const data = await Products.getProducts(params);
        const product = getProductWithId(id, getProductPrices(data.result, estimation));
        if (!product)
            return Promise.reject({
                missing: true,
                response_status: 200,
            });
        return product;
    },
    getProducts: async (params) => {
        if (!params.code) {
            params.code = "default";
        }
        const searchParams = new URLSearchParams(Object.assign({ "product-api": "postarealookup" }, params)).toString();
        return api(createUrl(searchParams), { method: "GET" });
    },
    getInvoiceMethods: async (params) => {
        if (!params.code) {
            params.code = "default";
        }
        const searchParams = new URLSearchParams(Object.assign({ "product-api": "invoicemethods" }, params)).toString();
        return api(createUrl(searchParams), { method: "GET" });
    },
    getProperties: async (params) => {
        if (!params.code) {
            params.code = "default";
        }
        const searchParams = new URLSearchParams(Object.assign({ "product-api": "Properties" }, params)).toString();
        return api(createUrl(searchParams), { method: "GET" });
    },
    pinLookup: async (socialNumber) => {
        const searchParams = new URLSearchParams({
            "product-api": "pinlookup",
            pin: socialNumber.toString(),
        }).toString();
        const data = await api(createUrl(searchParams), { method: "GET" });
        return data;
    },
    signProduct: async ({ data, bankId, signData, signStatus, signWithQRCode, }) => {
        const searchParams = new URLSearchParams({ "product-api": "sign" });
        if (bankId) {
            const status = await Products.signBankId({ data, signData, signStatus, signWithQRCode });
            if (status.result.SignedStatus !== 1 || status.result.ProgressStatusCode !== "COMPLETE") {
                return Promise.reject({ signError: true });
            }
            searchParams.append("bankIdOrderRef", status.result.OrderRef);
        }
        setProductParameters(searchParams, data);
        return api(createUrl(searchParams.toString()), { method: "GET" });
    },
    sendProductEmail: async ({ data, bankId, signData, signStatus, signWithQRCode, }) => {
        const basePath = "/wp-json/email/businessflow";
        const searchParams = new URLSearchParams();
        if (bankId) {
            const status = await Products.signBankId({ data, signData, signStatus, signWithQRCode });
            if (status.result.SignedStatus !== 1 || status.result.ProgressStatusCode !== "COMPLETE") {
                return Promise.reject({ signError: true });
            }
            searchParams.append("bankIdOrderRef", status.result.OrderRef);
        }
        setProductParameters(searchParams, data);
        const path = `${basePath}?${searchParams.toString()}`;
        const url = new URL(path, window.location.origin);
        return api(url, { method: "GET" });
    },
    signBankId: async ({ data, signData, signStatus, signWithQRCode, }) => {
        var _a;
        let useQRCode = true;
        const searchParams = new URLSearchParams({
            "product-api": "signstart",
            attempt: "0",
            signWithQRCode: useQRCode.toString(),
            date: new Date().getTime().toString(),
        });
        setProductParameters(searchParams, data);
        const response = await api(createUrl(searchParams.toString()), {
            method: "GET",
        });
        if (!((_a = response === null || response === void 0 ? void 0 : response.result) === null || _a === void 0 ? void 0 : _a.BankIdOrderRef))
            return Promise.reject({ signError: true });
        let result = response.result;
        signStatus.set("WAITINGFORSIGN");
        signData.subscribe((data) => {
            result = data;
        });
        signData.set(response.result);
        const pollingParams = new URLSearchParams({
            "product-api": "SignStatus",
            BankIdOrderRef: result.BankIdOrderRef,
            BankIdAutoStartToken: result.BankIdAutoStartToken,
            responseDate: result.responseDate,
            signWithQRCode: useQRCode.toString(),
            ref: result.BankIdOrderRef,
        });
        signWithQRCode.subscribe((isQR) => {
            useQRCode = isQR;
            pollingParams.set("signWithQRCode", isQR.toString());
        });
        return await poll({
            fn: (attemt) => {
                pollingParams.set("attempt", attemt.toString());
                return api(createUrl(pollingParams.toString()), { method: "GET" });
            },
            validate: (data) => {
                var _a;
                if ((_a = data.result) === null || _a === void 0 ? void 0 : _a.base64QRCode) {
                    signData.update((sData) => {
                        return Object.assign(Object.assign({}, sData), { base64QRCode: data.result.base64QRCode });
                    });
                }
                return data.result.ProgressStatusCode !== "PENDING" && data.result.SignedStatus !== 0;
            },
            interval: 1500,
            maxAttempts: 30,
            signStatus,
        });
    },
};
const cancelSigning = async (signData) => {
    const cancelParams = new URLSearchParams({
        "product-api": "cancelsign",
        bankIdOrderRef: signData.BankIdOrderRef,
    });
    return await api(createUrl(cancelParams.toString()), { method: "GET" });
};
export const useCancelSigning = (options) => useMutation((signData) => cancelSigning(signData), Object.assign({}, options));
export const useFetchProduct = (productId, estimation, params, options) => useQuery("updateProducts", () => Products.getProduct(productId, estimation, params), Object.assign({ cacheTime: 0, refetchOnWindowFocus: false }, options));
export const useFetchMicro = (params, options) => useQuery(["microproduktion", params], () => Products.getProducts(params), Object.assign({ refetchOnWindowFocus: false, cacheTime: 0 }, options));
export const useGetProducts = (params, options) => useQuery(["products", params], () => Products.getProducts(params), Object.assign({ retry: (count, error) => count < 3 && error.response_status != 200 }, options));
export const useGetInvoiceMethods = (params, options) => useQuery(["invoicemethods", params], () => Products.getInvoiceMethods(params), Object.assign({ retry: (count, error) => count < 3 && error.response_status != 200 }, options));
export const useGetProperties = (params, options) => useQuery(["properties", params], () => Products.getProperties(params), Object.assign({ retry: (count, error) => count < 3 && error.response_status != 200 }, options));
export const useCheckPurchase = (params, options) => useQuery(["checkPurchase", params], (offerId) => {
    const searchParams = new URLSearchParams(Object.assign({ "product-api": "CheckPurchase" }, params)).toString();
    return api(createUrl(searchParams), { method: "GET" });
}, Object.assign({}, options));
